import 'owa-shared-bootstrap/lib/polyfills';
import type { AppBootstrapOptions } from 'owa-app-module/lib/types/AppBootstrapOptions';
import 'owa-application';
import MailModule from 'owa-mail-module-view/lib/components/MailModule';
import { shouldRenderCommanding } from 'owa-mail-module-view/lib/utils/shouldRenderCommanding';
import getMailHeaderProps from 'owa-mail-module-view/lib/components/getMailHeaderProps';
import { GroupCommandBarView } from 'owa-mail-module-view/lib/components/lazy/lazyFunctions';
import MailCommandToolbar from 'owa-mail-module-view/lib/components/MailCommandToolbar';
import { mailModuleName } from './strings.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import initializeMailModuleState from './initializeMailModuleState';
import { mailRouterOptions } from './routing/mailRoutes';
import { OwaWorkload, Module } from 'owa-workloads';
import { controlIconRegistration } from 'owa-control-icons';
import { mailIconsRegistration } from 'owa-mail-icons';
import { addinIconsRegistration } from 'owa-addins-icons';
import { surfaceActionIconsRegistration } from 'owa-surface-action-icons';
import { getAnalyticOptions } from 'owa-shared-analytics';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import { lazySetupMailModulePostRender } from 'owa-mail-post-boot';
import type { BootStrategies } from 'owa-shared-start';
import type { SearchBoxContainerHandle } from 'owa-search';
import { selectDefaultFolder } from 'owa-mail-folder-forest-actions';
import { getInRibbonMode } from 'owa-command-ribbon-store';
import { lazyGetMailAppRibbonProps } from 'owa-mail-ribbon';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import { isGroupSelected } from 'owa-group-utils';
import groupHeaderCommandBarStore from 'owa-group-header-store/lib/store/CommandBarStore';
import GroupHeaderNavigationButton from 'owa-group-header-store/lib/store/schema/NavigationButton';
import { StrategyContext } from 'owa-strategies';
import {
    MailListItemCondensedThreeColumn,
    MailListItemFullThreeColumn,
    MailListItemSingleLineView,
} from 'owa-mail-list-item';

// Initialize mail packages here:
import 'owa-mail-list-selection-actions-v2';
import 'owa-mail-nudge-actions';
import 'owa-mail-folder-orchestration';
import 'owa-public-folder-favorite';
// TODO VSO 82257: Explicitly call code to set up selected calendars in boot rather than relying on 'owa-calendar-module-selected-calendars-user-config' orchestrators
import 'owa-calendar-module-selected-calendars-user-config';
import 'owa-shared-folder';

export async function getMailBootstrapOptions(
    sessionPromise: Promise<SessionData | undefined>,
    strategies?: BootStrategies
): Promise<AppBootstrapOptions> {
    const {
        mailListItemCondensedThreeColumn,
        mailListItemFullThreeColumn,
        mailListItemSingleLineView,
    } = await getMailListItemComponents(strategies);

    return {
        initializeState: initializeMailModuleState,
        sessionPromise,
        routerOptions: mailRouterOptions,
        getModuleName: () => loc(mailModuleName),
        workload: OwaWorkload.Mail,
        analyticsOptions: getAnalyticOptions(),
        iconFonts: [
            controlIconRegistration,
            mailIconsRegistration,
            addinIconsRegistration,
            surfaceActionIconsRegistration,
        ],
        postLazyAction: lazySetupMailModulePostRender,
        strategies,
        appModuleProps: {
            module: Module.Mail,
            renderModule: (searchBoxRef: React.RefObject<SearchBoxContainerHandle>) => (
                <StrategyContext.Provider
                    value={{
                        mailListItemCondensedThreeColumn: mailListItemCondensedThreeColumn,
                        mailListItemFullThreeColumn: mailListItemFullThreeColumn,
                        mailListItemSingleLineView: mailListItemSingleLineView,
                    }}>
                    <MailModule searchBoxRef={searchBoxRef} />
                </StrategyContext.Provider>
            ),
            activeModuleAction: () => selectDefaultFolder('ModuleSwitcher'),
            getHeaderInfo: getMailHeaderProps,
            isSkiplinkEnabled: () => true,
            lazyAppRibbonProps: lazyGetMailAppRibbonProps,
            command: {
                isEnabled: shouldRenderCommanding,
                shouldRenderRibbon: getInRibbonMode,
                renderToolbar: () =>
                    isGroupsEnabled() &&
                    isGroupSelected() &&
                    groupHeaderCommandBarStore.navigationButtonSelected !=
                        GroupHeaderNavigationButton.Email ? (
                        <GroupCommandBarView />
                    ) : (
                        <MailCommandToolbar />
                    ),
            },
        },
    };
}

const getMailListItemComponents = async (strategies?: BootStrategies) => {
    let mailListItemCondensedThreeColumn = await strategies?.mailListItemCondensedThreeColumnProvider?.import();
    if (!mailListItemCondensedThreeColumn) {
        mailListItemCondensedThreeColumn = MailListItemCondensedThreeColumn;
    }

    let mailListItemFullThreeColumn = await strategies?.mailListItemFullThreeColumnProvider?.import();
    if (!mailListItemFullThreeColumn) {
        mailListItemFullThreeColumn = MailListItemFullThreeColumn;
    }

    let mailListItemSingleLineView = await strategies?.mailListItemSingleLineViewProvider?.import();
    if (!mailListItemSingleLineView) {
        mailListItemSingleLineView = MailListItemSingleLineView;
    }

    return {
        mailListItemCondensedThreeColumn,
        mailListItemFullThreeColumn,
        mailListItemSingleLineView,
    };
};
